import { useEnumHelpers } from '@/queries/useEnumHelpers';
import { Box, Divider, Stack, Typography } from '@mui/material';
import React from 'react';
import { PaperTooltip } from '../../components/PaperTooltip.tsx';
import { ProgressBar } from '../../components/ProgressBar.tsx';
import { BuildingAlternative } from '../../stores/SubprojectStore.ts';
import { theme } from '../../theme/theme.ts';
import { formattedPercent, safeFormatDecimal } from '../../utils.ts';
import { EnergyKind, ExternalEnergy, OnsiteEnergy } from '../types';
import { iconForEnergySink } from '../utils.ts';
import { computeDemandSections } from './energyUtils';

interface EnergySupplyChartsProps {
  alternative: BuildingAlternative;
  onsiteProviders: OnsiteEnergy[];
  externalProviders: ExternalEnergy[];
  findOnsite: (id: string) => OnsiteEnergy | undefined;
}

export const EnergySupplyCharts: React.FC<EnergySupplyChartsProps> = ({
  alternative,
  onsiteProviders,
  externalProviders,
  findOnsite,
}) => {
  const { energySources } = useEnumHelpers();

  const demandSections = computeDemandSections(
    alternative,
    onsiteProviders,
    externalProviders,
  );

  return (
    <Box
      sx={{
        mt: 5,
        mb: 2,
        gridArea: 'usage',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      {demandSections.map(({ key, value, displayName }) => {
        const Icon = iconForEnergySink(key as EnergyKind);

        const selectedOnsiteGeneratorsEnergyOutput =
          alternative.onSiteEnergy.params.reduce(
            (accumulator, onsiteSystem) => {
              const onsiteSystemDetailed = findOnsite(onsiteSystem.techId);

              if (onsiteSystemDetailed) {
                return (
                  accumulator +
                  Number(onsiteSystemDetailed?.annualEnergyProduced) *
                    onsiteSystem.units
                );
              } else {
                return accumulator;
              }
            },
            0,
          );

        // External accounts for any remaining power, and can be 100% if the only thing selected...
        const accountsFor = externalProviders.filter((a) => {
          if (key === 'Natural Gas') {
            // Include all gas types
            return ['Natural Gas', 'Propane', 'Diesel'].includes(
              a.energyProvider,
            );
          }
          return a.energyProvider === key;
        });

        // When demand is negligible (< 0.001 MWh), show full bars automatically (100% satisfied)
        let onsitePct = 0;
        let externalPct = 0;

        if (value < 0.001) {
          // Negligible or no demand - automatically 100% satisfied (show as external coverage)
          externalPct = 1;
        } else {
          // Normal calculation when there is demand
          onsitePct =
            key === 'Electricity'
              ? selectedOnsiteGeneratorsEnergyOutput / value
              : 0;
          externalPct = accountsFor.length > 0 ? 1 - onsitePct : 0;
        }

        const totalCoverage = Math.min(onsitePct + externalPct, 1);

        // Debug logging
        if (process.env.NODE_ENV === 'development') {
          console.log(`Energy type: ${key}`, {
            demand: value,
            onsitePct,
            externalPct,
            totalCoverage,
            accountsForLength: accountsFor.length,
            selectedOnsiteOutput: selectedOnsiteGeneratorsEnergyOutput,
          });
        }

        return (
          <Box key={key}>
            <Stack
              direction={'row'}
              spacing={1}
              alignItems={'center'}
              justifyContent={'space-between'}
              minHeight={30}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Icon color={'info'} />
                <Typography variant={'body2'}>
                  {displayName ||
                    energySources.find((k) => k.name === key)?.name}{' '}
                  Supply
                </Typography>
              </Box>
              <Typography variant={'body2'}>
                {safeFormatDecimal(Number(value), 'EnergyDemand.mwhNeeded')} MWh
                Needed
              </Typography>
            </Stack>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Box
                key={`progress-${key}-${onsitePct}-${accountsFor.length}`}
                className={'progress-bar'}
                sx={{
                  display: 'flex',
                  width: '100%',
                  height: 32,
                  backgroundColor: theme.palette.grey[800],
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <PaperTooltip
                  title={
                    <Typography>
                      {formattedPercent(onsitePct)} - Onsite Energy Supply
                    </Typography>
                  }
                >
                  <ProgressBar
                    pct={isFinite(onsitePct) ? onsitePct : 0}
                    delay={0}
                    color={theme.palette.success.main}
                    onMouseEnter={() => {
                      for (const onsiteProvider of onsiteProviders) {
                        const unit = document.querySelector(
                          `div[data-id="${onsiteProvider.id}"]`,
                        );
                        if (!unit) {
                          continue;
                        }

                        if (!unit.classList.contains('highlight')) {
                          unit.classList.toggle('highlight');
                        }
                      }
                    }}
                    onMouseLeave={() => {
                      for (const onsiteProvider of onsiteProviders) {
                        const unit = document.querySelector(
                          `div[data-id="${onsiteProvider.id}"]`,
                        );
                        if (!unit) {
                          continue;
                        }

                        if (unit.classList.contains('highlight')) {
                          unit.classList.toggle('highlight');
                        }
                      }
                    }}
                  />
                </PaperTooltip>
                {accountsFor.length > 0 ? (
                  accountsFor.map((v, idx) => {
                    return (
                      <PaperTooltip
                        key={v.id}
                        title={
                          <Typography>
                            {formattedPercent(
                              (1 - onsitePct) / accountsFor.length,
                            )}{' '}
                            - {v.name}
                          </Typography>
                        }
                      >
                        <ProgressBar
                          color={
                            [
                              theme.palette.info.main,
                              theme.palette.warning.main,
                              theme.palette.error.main,
                              theme.palette.secondary.main,
                            ][idx % 4]
                          }
                          pct={
                            isFinite((1 - onsitePct) / accountsFor.length)
                              ? (1 - onsitePct) / accountsFor.length
                              : 0
                          }
                          delay={0}
                          onMouseEnter={() => {
                            const unit = document.querySelector(
                              `div[data-id="${v.id}"]`,
                            );
                            if (!unit) {
                              return;
                            }

                            if (!unit.classList.contains('highlight')) {
                              unit.classList.toggle('highlight');
                            }
                          }}
                          onMouseLeave={() => {
                            const unit = document.querySelector(
                              `div[data-id="${v.id}"]`,
                            );
                            if (!unit) {
                              return;
                            }

                            if (unit.classList.contains('highlight')) {
                              unit.classList.toggle('highlight');
                            }
                          }}
                        />
                      </PaperTooltip>
                    );
                  })
                ) : externalPct > 0 ? (
                  <PaperTooltip
                    title={
                      <Typography>
                        {formattedPercent(externalPct)} - Satisfied (No
                        providers needed)
                      </Typography>
                    }
                  >
                    <ProgressBar
                      pct={externalPct}
                      delay={0}
                      color={theme.palette.info.main}
                    />
                  </PaperTooltip>
                ) : null}
              </Box>
              <Typography fontSize={'large'} sx={{ minWidth: '3rem' }}>
                {formattedPercent(totalCoverage)}
              </Typography>
            </Box>
          </Box>
        );
      })}
      <Divider />
    </Box>
  );
};
